import { useEffect, useState } from 'react';

import { Clock, RotateCcw, Save } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';

import { SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO, useSlaAprovacaoSme } from './dados';

/**
 * A tela de parâmetro do SLA de aprovação SME.
 *
 * Define o prazo (em dias úteis) que a SME tem para analisar um lote enviado pela
 * DRE — afeta o indicador de prazo nas listas de apontamentos SME e o card de
 * demanda (apuração mensal, Fase 5, ainda não construída).
 *
 * A LEITURA É REAL, O SALVAR NÃO É. `/configuracoes/sla-aprovacao-sme` ainda não
 * existe na API: a busca do valor atual vai para a rede de verdade e mostra o erro
 * de verdade quando falha (ver `dados.ts`). Já "Salvar" nunca chamou API nenhuma
 * neste repositório — nenhuma tabela de configuração aceita escrita hoje — então o
 * botão mostra o aviso honesto de indisponibilidade em vez de fingir que gravou
 * (mesmo padrão de `operacao/credenciados/BarraDeAcoesEmLote.tsx`). "Restaurar
 * padrão" é o único botão que muda algo de fato: é so o estado local do campo.
 */

function clamp(valor: number): number {
  return Math.max(
    1,
    Math.min(30, Number.isFinite(valor) ? valor : SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO),
  );
}

export function SlaAprovacaoSmePage() {
  const [dias, setDias] = useState<number>(SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO);

  const consulta = useSlaAprovacaoSme();

  useEffect(() => {
    if (consulta.data) {
      setDias(clamp(consulta.data.diasUteis));
    }
  }, [consulta.data]);

  const restaurarPadrao = () => setDias(SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO);

  const salvar = () => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);

  return (
    <div className="space-y-6">
      <PageHeader
        title="SLA de Aprovação SME"
        description="Define o prazo (em dias úteis) que a SME tem para analisar um lote enviado pela DRE. Afeta o indicador de prazo nas listas de Apontamentos SME e o card de demanda."
      />

      <Card className="max-w-xl">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-primary" aria-hidden="true" />
            Dias úteis para aprovação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {consulta.isLoading ? (
            <Spinner />
          ) : consulta.isError ? (
            <ErrorState
              variant="load-failed"
              title="Não foi possível carregar o SLA de aprovação SME"
              primaryAction={{ label: 'Tentar de novo', onClick: () => consulta.refetch() }}
            />
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="sla-dias">Prazo (dias úteis)</Label>
                <Input
                  id="sla-dias"
                  type="number"
                  min={1}
                  max={30}
                  value={dias}
                  onChange={(evento) => setDias(clamp(Number(evento.target.value) || 1))}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Valor padrão: <strong>{SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO}</strong> dias úteis.
                  Aceita de 1 a 30. Lotes enviados antes da mudança continuam usando o novo prazo a
                  partir do próximo cálculo (atualização em até 5 minutos).
                </p>
              </div>
              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={restaurarPadrao}>
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Restaurar padrão ({SLA_APROVACAO_SME_DIAS_UTEIS_PADRAO})
                </Button>
                <Button onClick={salvar}>
                  <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Salvar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
